/**
 * examOfflineQueue — Iter-368 PWA-offline submit queue helper.
 *
 * Stores exam submissions in localStorage when the device is offline, and
 * replays them through /api/copilot/offline-submit once the device is back
 * online. Each entry carries a `client_uuid` so the backend can de-dup the
 * same submission across multiple replay attempts.
 *
 * Usage (from the exam UI):
 *
 *   import { queueOfflineSubmit, syncOfflineSubmits } from '@/lib/examOfflineQueue';
 *
 *   // On submit click — try online first, fall back to queue
 *   try {
 *     await api.post('/school/online/submit', body);
 *   } catch {
 *     queueOfflineSubmit(body);
 *   }
 *
 *   // Mount-once in App.jsx: kick a sync attempt as soon as we have network
 *   useEffect(() => {
 *     window.addEventListener('online', syncOfflineSubmits);
 *     syncOfflineSubmits();
 *     return () => window.removeEventListener('online', syncOfflineSubmits);
 *   }, []);
 */
import { authHeaders } from '@/lib/authToken';

const STORAGE_KEY = 'examOfflineQueue.v1';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const _read = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const _write = (rows) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows.slice(0, 100)));
  } catch (e) {
    /* localStorage may be full — drop the oldest entries */
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rows.slice(-50))); } catch { /* give up */ }
  }
};

const _genUuid = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `xq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const queueOfflineSubmit = (submission) => {
  if (!submission || !submission.tenant_id || !submission.exam_id || !submission.student_id) {
    console.warn('[examOfflineQueue] dropping malformed submission', submission);
    return null;
  }
  const entry = {
    ...submission,
    client_uuid: submission.client_uuid || _genUuid(),
    queued_at: new Date().toISOString(),
  };
  const rows = _read();
  // dedupe by client_uuid
  if (!rows.some((r) => r.client_uuid === entry.client_uuid)) {
    rows.push(entry);
    _write(rows);
  }
  return entry.client_uuid;
};

export const pendingOfflineCount = () => _read().length;

export const syncOfflineSubmits = async () => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { synced: 0, skipped: 0 };
  const rows = _read();
  if (rows.length === 0) return { synced: 0, skipped: 0 };
  let synced = 0;
  let skipped = 0;
  const remaining = [];
  for (const row of rows) {
    try {
      const r = await fetch(`${API_URL}/api/copilot/offline-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(row),
      });
      if (r.ok) {
        synced += 1;
        continue;
      }
      if (r.status === 401 || r.status === 403) {
        skipped += 1;
        remaining.push(row);
        continue;
      }
      // Other errors — keep, retry next round
      remaining.push(row);
    } catch {
      // Network blip mid-sync — bail and keep the rest queued
      remaining.push(row);
      break;
    }
  }
  _write(remaining);
  return { synced, skipped, remaining: remaining.length };
};

export default { queueOfflineSubmit, syncOfflineSubmits, pendingOfflineCount };
